import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import CodeEditor from '@uiw/react-textarea-code-editor';
import { QRCodeSVG } from 'qrcode.react';
import { 
  Copy, 
  Download, 
  Share2, 
  Lock,
  Clock,
  FileCode,
  ArrowLeft,
  Eye,
  Code,
  AlertTriangle,
  CheckCircle,
  FileDown,
  Image as ImageIcon,
  Edit,
  Star,
  ChevronDown,
  Printer,
  QrCode,
  X,
  Twitter,
  Facebook,
  Linkedin,
  Mail,
  BookOpen,
  Flame
} from 'lucide-react';
import toast from 'react-hot-toast';
import { decodeData, isExpired, verifyPassword } from '../utils/storage';
import { languages, getFileExtension } from '../utils/languages';
import { exportToPDF, exportToHTML } from '../utils/export';
import { addToFavorites, isFavorite as checkFavorite } from '../utils/history';
import { updatePasteSEO } from '../hooks/useSEO';
import BackButton from './BackButton';

const Viewer = ({ theme }) => {
  const { data } = useParams();
  const navigate = useNavigate();
  const [paste, setPaste] = useState(null);
  const [error, setError] = useState(null);
  const [isLocked, setIsLocked] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [showRaw, setShowRaw] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [isFav, setIsFav] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [showBurnWarning, setShowBurnWarning] = useState(false);
  const [viewCount, setViewCount] = useState(0);
  
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!data) {
      setError('No paste data found');
      return;
    }

    const decoded = decodeData(data);
    
    if (!decoded) {
      setError('Invalid or corrupted paste data');
      return;
    }

    if (isExpired(decoded.expiresAt)) {
      setError('This paste has expired');
      return;
    }

    // Handle burn after read
    if (decoded.burnAfterRead) {
      const burnKey = `burned_${decoded.id}`;
      const wasBurned = localStorage.getItem(burnKey);
      
      if (wasBurned) {
        setError('This paste has been burned and is no longer available');
        return;
      }
      
      // Show warning before revealing content
      setShowBurnWarning(true);
    }

    // Track view count locally
    const viewKey = `views_${decoded.id}`;
    const currentViews = parseInt(localStorage.getItem(viewKey) || '0', 10);
    const newViewCount = currentViews + 1;
    localStorage.setItem(viewKey, newViewCount.toString());
    setViewCount(newViewCount);

    if (decoded.passwordHash) {
      setIsLocked(true);
    }

    setPaste(decoded);
    
    // Update SEO meta tags for the paste
    updatePasteSEO(decoded);
    
    // Check if favorite
    checkFavorite(decoded.id).then(setIsFav);
  }, [data]);

  // Confirm viewing burn-after-read paste
  const confirmBurnView = () => {
    if (paste) {
      const burnKey = `burned_${paste.id}`;
      localStorage.setItem(burnKey, 'true');
      setShowBurnWarning(false);
      toast.success('This paste will be destroyed after you leave this page', { duration: 5000 });
    }
  };

  const handleUnlock = async () => {
    if (!passwordInput) {
      toast.error('Please enter a password');
      return;
    }

    setVerifying(true);
    const isValid = await verifyPassword(passwordInput, paste.passwordHash);
    setVerifying(false);

    if (isValid) {
      setIsLocked(false);
      toast.success('Paste unlocked!');
    } else {
      toast.error('Incorrect password');
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(paste.content);
    toast.success('Content copied to clipboard!');
  };

  const handleCopyLink = async () => {
    await navigator.clipboard.writeText(window.location.href);
    toast.success('Share link copied!');
  };

  const handleDownload = () => {
    const blob = new Blob([paste.content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${paste.title || 'scrawl'}.${getFileExtension(paste.language)}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('File downloaded!');
  };

  const handleExportPDF = async () => {
    try {
      toast.loading('Generating PDF...');
      await exportToPDF(paste.content, paste.title, { 
        language: paste.language, 
        theme 
      });
      toast.dismiss();
      toast.success('PDF exported successfully!');
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to export PDF');
    }
    setShowExportMenu(false);
  };

  const handleExportHTML = () => {
    exportToHTML(paste.content, paste.title, { 
      language: paste.language, 
      theme 
    });
    toast.success('HTML file downloaded!');
    setShowExportMenu(false);
  };

  const handleAddToFavorites = async () => {
    await addToFavorites(paste);
    setIsFav(true);
    toast.success('Added to favorites!');
  };

  // Print content
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${paste.title || 'Scrawl'}</title>
          <style>
            body { font-family: 'Segoe UI', sans-serif; padding: 2rem; max-width: 800px; margin: 0 auto; }
            pre { background: #f5f5f5; padding: 1rem; overflow-x: auto; }
            h1 { border-bottom: 2px solid #333; padding-bottom: 0.5rem; }
          </style>
        </head>
        <body>
          <h1>${paste.title || 'Untitled'}</h1>
          ${paste.editorMode === 'rich' ? paste.content : `<pre><code>${paste.content.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</code></pre>`}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // Calculate reading time
  const getReadingTime = () => {
    if (!paste?.content) return '< 1 min';
    const text = paste.content.replace(/<[^>]*>/g, '');
    const words = text.trim().split(/\s+/).filter(w => w.length > 0).length;
    const minutes = Math.ceil(words / 200);
    return minutes < 1 ? '< 1 min' : `${minutes} min read`;
  };

  // Calculate word count
  const getWordCount = () => {
    if (!paste?.content) return 0;
    const text = paste.content.replace(/<[^>]*>/g, '');
    return text.trim().split(/\s+/).filter(w => w.length > 0).length;
  };

  // Social sharing
  const shareToSocial = (platform) => {
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent(paste.title || 'Check out my paste on Scrawl');
    
    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      email: `mailto:?subject=${text}&body=${url}`
    };
    
    window.open(urls[platform], '_blank', 'width=600,height=400');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getLanguageLabel = (value) => {
    const lang = languages.find(l => l.value === value);
    return lang ? lang.label : 'Plain Text';
  };

  const lineCount = paste?.content?.split('\n').length || 0;

  if (error) {
    return (
      <div className="viewer-error">
        <AlertTriangle size={48} />
        <h2>Oops!</h2>
        <p>{error}</p>
        <Link to="/" className="action-btn primary">
          <ArrowLeft size={16} />
          Create New Paste
        </Link>
      </div>
    );
  }

  if (!paste) {
    return (
      <div className="viewer-loading">
        <div className="spinner"></div>
        <p>Loading paste...</p>
      </div>
    );
  }

  if (isLocked) {
    return (
      <div className="viewer-locked">
        <Lock size={48} />
        <h2>This paste is password protected</h2>
        <p>Enter the password to view the content</p>
        <div className="unlock-form">
          <input
            type="password"
            placeholder="Enter password..."
            value={passwordInput}
            onChange={(e) => setPasswordInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleUnlock()}
            className="password-input"
          />
          <button 
            className="action-btn primary" 
            onClick={handleUnlock}
            disabled={verifying}
          >
            {verifying ? 'Verifying...' : 'Unlock'}
          </button>
        </div>
        <Link to="/" className="back-link">
          <ArrowLeft size={16} />
          Create New Paste
        </Link>
      </div>
    );
  }

  // Show burn warning before revealing content
  if (showBurnWarning) {
    return (
      <div className="viewer-locked burn-warning-screen">
        <Flame size={48} className="burn-icon" />
        <h2>⚠️ Burn After Reading</h2>
        <p>This paste will be <strong>permanently destroyed</strong> after you view it.</p>
        <p className="burn-warning-text">Once revealed, this content cannot be viewed again by anyone.</p>
        <div className="burn-actions">
          <button 
            className="action-btn primary"
            onClick={confirmBurnView}
          >
            <Eye size={16} />
            View & Burn
          </button>
          <Link to="/" className="action-btn secondary">
            <ArrowLeft size={16} />
            Go Back
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="viewer-container">
      <div className="viewer-header">
        <div className="viewer-header-top">
          <BackButton />
        </div>
        <div className="viewer-info">
          <h1 className="viewer-title">{paste.title || 'Untitled'}</h1>
          <div className="viewer-meta">
            <span className="meta-item">
              <FileCode size={14} />
              {getLanguageLabel(paste.language)}
            </span>
            {paste.editorMode === 'rich' && (
              <span className="meta-item rich-text-badge">
                <Code size={14} />
                Rich Text
              </span>
            )}
            <span className="meta-item">
              <Clock size={14} />
              {formatDate(paste.createdAt)}
            </span>
            <span className="meta-item">
              <BookOpen size={14} />
              {getReadingTime()}
            </span>
            <span className="meta-item">
              {getWordCount()} words
            </span>
            <span className="meta-item views">
              <EyeIcon size={14} />
              {viewCount} view{viewCount !== 1 ? 's' : ''}
            </span>
            {paste.burnAfterRead && (
              <span className="meta-item burn-badge">
                <Flame size={14} />
                Burns after reading
              </span>
            )}
            {paste.expiresAt && (
              <span className="meta-item expiry">
                <AlertTriangle size={14} />
                Expires: {formatDate(paste.expiresAt)}
              </span>
            )}
            {paste.passwordHash && (
              <span className="meta-item protected">
                <CheckCircle size={14} />
                Password Protected
              </span>
            )}
            {paste.images?.length > 0 && (
              <span className="meta-item">
                <ImageIcon size={14} />
                {paste.images.length} image{paste.images.length > 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>

        <div className="viewer-actions">
          {!isFav && (
            <button 
              className="action-btn secondary"
              onClick={handleAddToFavorites}
              title="Add to favorites"
            >
              <Star size={16} />
              Save
            </button>
          )}
          {paste.editorMode !== 'rich' && (
            <button 
              type="button"
              className={`action-btn secondary ${showRaw ? 'active' : ''}`}
              onClick={() => setShowRaw(!showRaw)}
              title={showRaw ? 'Show highlighted' : 'Show raw'}
            >
              {showRaw ? <Code size={16} /> : <Eye size={16} />}
              {showRaw ? 'Highlighted' : 'Raw'}
            </button>
          )}
          <button type="button" className="action-btn secondary" onClick={handleCopy}>
            <Copy size={16} />
            Copy
          </button>
          
          {/* Export Dropdown */}
          <div className="dropdown" ref={dropdownRef}>
            <button 
              type="button"
              className="action-btn secondary"
              onClick={() => setShowExportMenu(!showExportMenu)}
            >
              <FileDown size={16} />
              Export
              <ChevronDown size={14} className={showExportMenu ? 'rotate' : ''} />
            </button>
            {showExportMenu && (
              <div className="dropdown-menu">
                <button type="button" onClick={handleDownload}>
                  <Download size={16} />
                  Download as File
                </button>
                <button type="button" onClick={handleExportPDF}>
                  <FileDown size={16} />
                  Export as PDF
                </button>
                <button type="button" onClick={handleExportHTML}>
                  <Code size={16} />
                  Export as HTML
                </button>
              </div>
            )}
          </div>

          <button type="button" className="action-btn secondary icon-only" onClick={handlePrint} title="Print">
            <Printer size={16} />
          </button>
          <button type="button" className="action-btn secondary icon-only" onClick={() => setShowQRModal(true)} title="QR Code">
            <QrCode size={16} />
          </button>

          <button type="button" className="action-btn primary" onClick={handleCopyLink}>
            <Share2 size={16} />
            Share
          </button>
        </div>
      </div>

      {/* QR Code & Social Share Modal */}
      {showQRModal && (
        <div className="modal-overlay" onClick={() => setShowQRModal(false)}>
          <div className="modal-content qr-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="modal-close" onClick={() => setShowQRModal(false)}>
              <X size={20} />
            </button>
            <h2>Share This Paste</h2>
            {window.location.href.length <= 2000 ? (
              <div className="qr-code-container">
                <QRCodeSVG value={window.location.href} size={200} level="L" />
              </div>
            ) : (
              <div className="qr-unavailable">
                <QrCode size={48} />
                <p>QR code unavailable for large pastes</p>
              </div>
            )}
            <p className="share-url">{window.location.href.length > 100 ? window.location.href.substring(0, 100) + '...' : window.location.href}</p>
            <button 
              type="button" 
              className="action-btn secondary copy-url-btn"
              onClick={() => { navigator.clipboard.writeText(window.location.href); toast.success('Link copied!'); }}
            >
              <Copy size={16} />
              Copy Link
            </button>
            <div className="social-share-buttons">
              <button type="button" className="social-btn twitter" onClick={() => shareToSocial('twitter')} title="Share on Twitter">
                <Twitter size={20} />
              </button>
              <button type="button" className="social-btn facebook" onClick={() => shareToSocial('facebook')} title="Share on Facebook">
                <Facebook size={20} />
              </button>
              <button type="button" className="social-btn linkedin" onClick={() => shareToSocial('linkedin')} title="Share on LinkedIn">
                <Linkedin size={20} />
              </button>
              <button type="button" className="social-btn email" onClick={() => shareToSocial('email')} title="Share via Email">
                <Mail size={20} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Attached Images */}
      {paste.images && paste.images.length > 0 && (
        <div className="viewer-images">
          <div className="images-header">
            <ImageIcon size={16} />
            <span>Attached Images</span>
          </div>
          <div className="images-grid">
            {paste.images.map((img) => (
              <div key={img.id} className="viewer-image">
                <img src={img.url} alt={img.name} />
                <div className="image-caption">{img.name}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="viewer-content">
        {paste.editorMode === 'rich' ? (
          <div 
            className="rich-content-view"
            dangerouslySetInnerHTML={{ __html: paste.content }}
          />
        ) : showRaw ? (
          <div className="raw-content">
            <div className="line-numbers">
              {Array.from({ length: lineCount }, (_, i) => (
                <span key={i + 1}>{i + 1}</span>
              ))}
            </div>
            <pre className="raw-text">{paste.content}</pre>
          </div>
        ) : (
          <div className="highlighted-content">
            <div className="line-numbers">
              {Array.from({ length: lineCount }, (_, i) => (
                <span key={i + 1}>{i + 1}</span>
              ))}
            </div>
            <CodeEditor
              value={paste.content}
              language={paste.language}
              readOnly
              data-color-mode={theme}
              padding={16}
              style={{
                flex: 1,
                fontSize: 14,
                fontFamily: '"JetBrains Mono", "Fira Code", "Consolas", monospace',
                backgroundColor: 'transparent',
              }}
            />
          </div>
        )}
      </div>

      <div className="viewer-footer">
        <Link to="/" className="action-btn secondary">
          <Edit size={16} />
          New Paste
        </Link>
        <div className="stats">
          <span>{lineCount} lines</span>
          <span>{paste.content.length} characters</span>
        </div>
      </div>
    </div>
  );
};

export default Viewer;
